import * as React from 'react'
import type { CSSProperties } from 'react'
import { cn } from '../lib'
import { typographyStyle } from '../lib/catalog'
import { designTokens } from '../lib/design-tokens'

type TextareaVariant = 'default' | 'subscriptionCancel' | 'thin'

type TextareaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
  variant?: TextareaVariant
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, variant = 'default', style, ...props }, ref) => {
    if (variant === 'subscriptionCancel' || variant === 'thin') {
      const tokenStyle: CSSProperties & { ['--textarea-placeholder']?: string } = {
        border: `${designTokens.borders.widths.thin} ${designTokens.borders.styles.solid} ${designTokens.colors.border.default}`,
        borderRadius: designTokens.borders.radius.sm,
        padding: `${designTokens.spacing.sm} ${designTokens.spacing.xs}`,
        minHeight: designTokens.sizes.height.subscriptionCancelTextarea,
        backgroundColor: designTokens.colors.background.surface,
        color:
          variant === 'thin' ? designTokens.colors.text.primary : designTokens.colors.text.strong,
        fontFamily: designTokens.typography.fontFamilies.primary,
        ...typographyStyle(designTokens.typography.subscriptionCancel.bodyTight),
        '--textarea-placeholder': designTokens.colors.text.additional,
      }

      return (
        <textarea
          ref={ref}
          className={cn(
            'flex w-full resize-none bg-transparent focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-[var(--textarea-placeholder)]',
            className
          )}
          style={{ ...tokenStyle, ...style }}
          {...props}
        />
      )
    }

    type TextareaTokenStyle = CSSProperties & { [key in `--textarea-${string}`]?: string }
    const tokenStyle: TextareaTokenStyle = {
      '--textarea-bg': designTokens.colors.background.inputDefault,
      '--textarea-bg-disabled': designTokens.colors.background.inputDisabled,
      '--textarea-border': designTokens.colors.border.inputDefault,
      '--textarea-border-focus': designTokens.colors.border.inputFocus,
      '--textarea-border-disabled': designTokens.colors.border.inputDisabled,
      '--textarea-text': designTokens.colors.text.inputValue,
      '--textarea-text-disabled': designTokens.colors.text.inputDisabled,
      '--textarea-placeholder': designTokens.colors.text.inputPlaceholder,
      '--textarea-ring': designTokens.colors.border.inputFocus,
      '--textarea-ring-offset': designTokens.colors.background.inputDefault,
      borderRadius: designTokens.borders.radius.sm,
      borderWidth: designTokens.borders.widths.thin,
      borderStyle: designTokens.borders.styles.solid,
      padding: `${designTokens.spacing.xs} ${designTokens.spacing.sm}`,
      fontFamily: designTokens.typography.fontFamilies.primary,
      ...typographyStyle(designTokens.typography.input.text),
    }

    return (
      <textarea
        ref={ref}
        className={cn(
          'flex min-h-[80px] w-full resize-none border bg-[var(--textarea-bg)] text-[var(--textarea-text)] placeholder:text-[var(--textarea-placeholder)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--textarea-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--textarea-ring-offset)] focus-visible:border-[var(--textarea-border-focus)] disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-[var(--textarea-bg-disabled)] disabled:text-[var(--textarea-text-disabled)] disabled:border-[var(--textarea-border-disabled)]',
          className
        )}
        style={{ ...tokenStyle, ...style }}
        {...props}
      />
    )
  }
)
Textarea.displayName = 'Textarea'

export { Textarea }
